<script setup lang="ts">
import { computed, watch, ref, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import { IonButton, IonContent } from '@ionic/vue';
import { useI18n } from 'vue-i18n';

import TopBar from '@/shared/components/TopBar.vue';
import TopUpItemsSummary from '@/shared/components/ui/TopUpItemsSummary.vue';
import hexRgb from 'hex-rgb';
import rgbHex from 'rgb-hex';
import { TopUp } from '@/enums/values.enum';

import BackButton from '@/shared/components/BackButton.vue';
import { useTransactionStore } from '@/stores/transaction/transactionStore';
import { useSharedStore } from '@/stores/shared/sharedStore';
import useTopUp from '@/modules/top-up/composables/useTopUp';
import useTenant from '@/shared/composables/useTenant';
import useWallet from '@/modules/wallet/composables/useWallet';
import { TransactionSubtype } from '@/enums/wallet.enum';
import useStorageService from '@/shared/composables/useStorageService';
import { WalletRfidCreationCost } from '@/shared/interfaces/wallet';
import type { PreferredPaymentMode } from '@/modules/top-up/composables/useTopUp';

const { t } = useI18n();
const route = useRoute();
const walletId = route.params.wallet_id?.toString();
const encryptedRfidTag = route.query.id?.toString();
const loginToken = route.query.loginToken?.toString() || '';
const topUpType = route.query.topUpType?.toString();
const packageId = route.query.packageId ? +route.query.packageId : 0;

const sharedStore = useSharedStore();
const { getTenantConfigAndStyles } = useTenant();
const { tenantConfig } = getTenantConfigAndStyles();
const { getMinimumTopUpAmount, getRfidCreationCost, selectedWallet } = useWallet();
const effectiveWalletId = computed(() => selectedWallet.value?.id?.toString() || walletId);
const { minimumTopUp } = getMinimumTopUpAmount(effectiveWalletId);
const { get } = useStorageService();
const rfidCreationCost = ref<WalletRfidCreationCost>();

const primary_color = window.primary_color;
const primaryRgb = hexRgb(window.primary_color, { format: 'array' });
const primaryShade = '#' + rgbHex(`rgb(${primaryRgb[0]}, ${primaryRgb[1]}, ${primaryRgb[2]})`);

const transactionStore = useTransactionStore();
const transaction = computed(() => transactionStore.getItemsAndTotal());

const { startTopUp, getApplicablePaymentCountry, formValue, selectTopUp } = useTopUp(tenantConfig, minimumTopUp);
const activePreferredPaymentMode = computed<PreferredPaymentMode>(() => {
	if (!sharedStore.shouldUsePreferredPaymentMode) return null;
	if (tenantConfig.value?.viva_wallet_click_to_pay_preferred_mode) return 'click_to_pay';
	if (tenantConfig.value?.viva_wallet_payconiq_preferred_mode) return 'payconiq';
	return null;
});

const visibleItems = computed(() => transactionStore.items);

const topUpLabel = computed(() => {
	if (transaction.value.subtype === TransactionSubtype.PACKAGE) return t('top_up.packages');
	if (transaction.value.subtype === TransactionSubtype.FREE_AMOUNT) return t('top_up.free_amount');
	return '';
});

const walletReceivesCurrency = computed(() => {
	if (selectedWallet.value && selectedWallet.value.currency.name !== 'EUR') {
		return selectedWallet.value.currency.prefix;
	}
	return '€';
});

const isPackageTopUp = computed(() => transaction.value.subtype === TransactionSubtype.PACKAGE);

const onContinue = async () => {
	formValue.value = transactionStore.totalPriceWithoutFee;

	const paymentMethodCountry = getApplicablePaymentCountry();
	if (topUpType === TopUp.TopUpTypeFreeAmount) {
		if (!selectedWallet.value) {
			selectedWallet.value = await get('selectedWallet');
		}
		startTopUp(paymentMethodCountry, activePreferredPaymentMode.value, encryptedRfidTag, effectiveWalletId.value, selectedWallet.value?.currency.id, loginToken);
	} else if (topUpType === TopUp.TopUpTypePackages) {
		selectTopUp(packageId, paymentMethodCountry, activePreferredPaymentMode.value, encryptedRfidTag, loginToken);
	}
};


onMounted(async () => {
	if (!selectedWallet.value) {
		selectedWallet.value = await get('selectedWallet');
	}
	if (effectiveWalletId.value) {
		rfidCreationCost.value = await getRfidCreationCost(effectiveWalletId.value);
		if (rfidCreationCost.value && rfidCreationCost.value.wallet_rfid_creation_cost > 0) {
			transactionStore.setRfidCost(+rfidCreationCost.value.wallet_rfid_creation_cost, t('transaction.card_creation_cost'));
			transactionStore.totalPriceNumber = transactionStore.totalPriceNumber + rfidCreationCost.value.wallet_rfid_creation_cost;
		}
	}
});
</script>

<template>
	<ion-content :style="`background: linear-gradient(${primary_color}, ${primaryShade});`">
		<div class="flex flex-col justify-between px-[34px] min-h-screen pb-[100px]">
			<div v-if="transactionStore.items.length">
				<TopBar>
					<template #left>
						<BackButton />
					</template>

					<template #center>
						<div class="flex grow items-center justify-center w-full">
							<span class="font-sourcesanspro-semibold text-l2">{{ t('topup.title') }}</span>
						</div>
					</template>
				</TopBar>

				<div class="flex flex-col items-center justify-center mb-8 mt-8">
					<h1 class="font-sourcesanspro-semibold text-lg2">{{ t('checkout.title') }}</h1>
					<span class="font-sourcesanspro-regular text-base2 text-white/50"> {{ visibleItems.length }} {{ t('checkout.items') }} </span>
					<span class="text-white/30 text-sm mt-1">({{ topUpLabel }})</span>
				</div>
				<TopUpItemsSummary
					:items="visibleItems"
					:total-price="transaction.totalPriceNumber"
					:wallet-receives="isPackageTopUp ? undefined : transaction.totalPriceWithoutFee"
					:wallet-receives-currency="walletReceivesCurrency"
					:title="t('checkout.order_details')"
					:total-title="t('checkout.items_total')"
				/>
			</div>

			<div class="fixed bottom-4 left-0 right-0 z-10 bg-transparent">
				<div class="max-w-[600px] mx-auto px-4 pt-4 pb-[env(safe-area-inset-bottom)]">
					<ion-button v-if="visibleItems.length > 0" :disabled="!visibleItems.length" class="w-full hover:rounded-full hover:bg-white" expand="full" size="large" shape="round" @click="onContinue">
						<div>{{ t('general.continue') }}</div>
					</ion-button>
				</div>
			</div>
		</div>
	</ion-content>
</template>

<style scoped></style>
